import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';

const normalizeVersionStatus = (value) => String(value ?? '').trim().toLowerCase();

export function normalizeFlowchartVersionOption(version) {
  if (typeof version !== 'object' || version == null) {
    const primitiveValue = version == null ? null : Number(version);
    return {
      value: Number.isNaN(primitiveValue) ? null : primitiveValue,
      status: '',
      isActive: false,
      flowchartVersionId: null,
      label:
        primitiveValue == null || Number.isNaN(primitiveValue) ? 'Default' : `v${primitiveValue}`
    };
  }

  const rawValue = version?.versionNumber ?? version?.version_number ?? version?.value ?? null;
  const normalizedValue = rawValue == null ? null : Number(rawValue);
  const rawStatus = version?.status ?? version?.versionStatus ?? '';
  const normalizedStatus = normalizeVersionStatus(rawStatus);
  const statusLabel = normalizedStatus
    ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)
    : '';
  const fallbackLabel = normalizedValue == null ? 'Default' : `v${normalizedValue}`;
  const label = version?.label || `${fallbackLabel}${statusLabel ? ` - ${statusLabel}` : ''}`;

  return {
    value: Number.isNaN(normalizedValue) ? null : normalizedValue,
    status: normalizedStatus,
    isActive:
      version?.isActive === true ||
      version?.is_active === true ||
      normalizedStatus === 'active',
    flowchartVersionId:
      version?.flowchartVersionId ||
      version?.flowchart_version_id ||
      version?.versionId ||
      version?.id ||
      null,
    label
  };
}

export async function fetchFlowchartVersions(cycleId, processId) {
  const response = await axiosInstance.get(API_ENDPOINTS.getFlowchartVersions(cycleId, processId));
  if (response?.data?.success !== true) return [];
  const rawVersions = response?.data?.versions;
  if (!Array.isArray(rawVersions)) return [];

  return rawVersions
    .map(normalizeFlowchartVersionOption)
    .filter((option) => option.label && (option.value == null || !Number.isNaN(option.value)));
}

export async function fetchUnmappedFlowchartRisks(processUuid, flowchartVersionId, filters = {}) {
  const params = {};
  const riskLevel = String(filters.riskLevel ?? '').trim();
  const source = String(filters.source ?? '').trim();
  const sourceRef = String(filters.sourceRefVersionId ?? '').trim();
  const search = String(filters.search ?? '').trim();
  const riskCategory = String(filters.riskCategory ?? '').trim();
  if (riskLevel && riskLevel !== 'all') {
    params.risk_level = riskLevel;
  }
  if (source && source !== 'all') {
    params.source = source;
  }
  if (sourceRef && sourceRef !== 'all') {
    params.source_ref = sourceRef;
  }
  if (search) {
    params.search = search;
  }
  if (riskCategory && riskCategory !== 'all') {
    params.risk_category = riskCategory;
  }

  const response = await axiosInstance.get(
    API_ENDPOINTS.getUnmappedFlowchartRisks(processUuid, flowchartVersionId),
    { params }
  );
  if (response?.data?.success !== true) {
    throw new Error(response?.data?.error || 'Failed to load process risks');
  }
  const rawRisks = Array.isArray(response?.data?.risks) ? response.data.risks : [];
  return rawRisks.map((risk) => ({
    id: risk.id,
    clientRiskId: risk.client_risk_id ?? risk.clientRiskId ?? null,
    riskId: risk.risk_id ?? risk.riskId ?? null,
    riskLevel: risk.risk_level ?? risk.riskLevel ?? null,
    riskDescription: risk.risk_description ?? risk.riskDescription ?? '',
    riskCategory: risk.risk_category ?? risk.riskCategory ?? null,
    source: risk.source ?? null,
    sourceRef: risk.source_ref ?? risk.sourceRef ?? null,
    sourceRefVersionNumber:
      risk.source_ref_version_number ?? risk.sourceRefVersionNumber ?? null,
    sourceRefVersionStatus:
      risk.source_ref_version_status ?? risk.sourceRefVersionStatus ?? null,
    nodes: (Array.isArray(risk.nodes) ? risk.nodes : []).map((node) => ({
      id: node.id,
      nodeType: node.nodeType ?? node.node_type ?? '',
      nodeDescription: node.nodeDescription ?? node.node_description ?? '',
      actor: node.actor ?? '',
      system: node.system ?? '',
      keySystem: node.keySystem ?? node.key_system ?? false,
      keyControl: node.keyControl ?? node.key_control ?? false,
    })),
    controls: (Array.isArray(risk.controls) ? risk.controls : []).map((control) => ({
      id: control.id,
      controlId: control.control_id ?? control.controlId ?? '',
      clientControlId: control.client_control_id ?? control.clientControlId ?? '',
      title: control.title ?? '',
    })),
  }));
}

export async function attachManualRiskToFlowchart({
  processId,
  flowchartVersionId,
  riskId,
  nodeIds,
  auditYearId,
}) {
  const response = await axiosInstance.post(API_ENDPOINTS.attachManualRiskToFlowchart, {
    process_id: processId,
    flowchart_version_id: flowchartVersionId,
    risk_id: riskId,
    node_ids: nodeIds,
    ...(auditYearId ? { audit_year_id: auditYearId } : {}),
  });
  if (response?.data?.success !== true) {
    throw new Error(response?.data?.error || 'Failed to link risk to subprocess');
  }
  return response.data;
}

export async function detachManualRiskFromFlowchart({
  processId,
  flowchartVersionId,
  riskId,
  nodeId,
}) {
  const response = await axiosInstance.delete(API_ENDPOINTS.detachManualRiskFromFlowchart, {
    data: {
      process_id: processId,
      flowchart_version_id: flowchartVersionId,
      risk_id: riskId,
      node_id: nodeId,
    },
  });
  if (response?.data?.success !== true) {
    throw new Error(response?.data?.error || 'Failed to unlink risk from subprocess');
  }
  return response.data;
}

export async function publishFlowchartVersion(flowchartVersionId) {
  const res = await axiosInstance.post(
    API_ENDPOINTS.publishFlowchartVersion(flowchartVersionId)
  );
  return res.data;
}

export async function setActiveFlowchartVersion(flowchartVersionId) {
  const res = await axiosInstance.post(
    API_ENDPOINTS.setActiveFlowchartVersion(flowchartVersionId)
  );
  return res.data;
}

// Persists per-version connector layout (handle attachments + waypoints)
// for one or more connectors. `layouts` is a partial array of:
//   { connectorId, sourceHandle?, targetHandle?, waypoints?, clearWaypoints? }
// — only the connectors the user actually edited get sent. The BE leaves
// every other connector mapping untouched.
export async function updateFlowchartConnectorLayout(flowchartVersionId, layouts) {
  if (!flowchartVersionId || !Array.isArray(layouts) || layouts.length === 0) return null;
  const res = await axiosInstance.patch(
    API_ENDPOINTS.updateFlowchartConnectorLayout(flowchartVersionId),
    { layouts }
  );
  return res.data;
}

// Persists per-actor swimlane heights for one flowchart version.
// `layouts` is an array of { actorName, swimlaneHeight }. The BE
// resolves the actor by name and updates every mapping row in this
// version pointing at that actor — one PATCH per debounced user save.
export async function updateFlowchartSwimlaneLayout(flowchartVersionId, layouts) {
  if (!flowchartVersionId || !Array.isArray(layouts) || layouts.length === 0) return null;
  const res = await axiosInstance.patch(
    API_ENDPOINTS.updateFlowchartSwimlaneLayout(flowchartVersionId),
    { layouts }
  );
  return res.data;
}
